import {Component, Vue} from 'vue-property-decorator';
import axios, {AxiosError, AxiosRequestConfig, AxiosResponse, CancelToken} from 'axios';

import axiosInstance from '@/axiosInstance';
import FormDataType from '@/store/types/FormDataType';
import {ApiException} from '@/api/Exception';
import Recaptcha from '@/components/recaptcha/Recaptcha.vue';

type FetchingInterface = {
	recaptchaP?: boolean
	contactP?: boolean
};

export {
	ApiException,
	FetchingInterface
};

@Component({})
export default class ApiMixin extends Vue {
	defaultFieldSuffix: string = 'Field';

	defaultException: ApiException = {
		status: 'unknown',
		code: 500,
		message: 'Došlo k chybě, zkuste to prosím znovu později.'
	};

	recaptchaUrl: string = 'recaptcha/verify';
	contactUrl: string = 'contact';
	getAppUrl: string = 'get-app';

	get locale(): string {
		return this.$store.state.locale;
	}

	get fetching(): FetchingInterface {
		return this.$store.state.fetching;
	}

	get recaptcha(): Recaptcha {
		return this.$store.state.recaptcha;
	}

	recaptchaT: Function = () => ({});
	contactT: Function = () => ({});
	getAppT: Function = () => ({});

	created(): void {
		axiosInstance.interceptors.request.use((request: AxiosRequestConfig) => {
			request.params = {
				...request.params,
				locale: this.locale
			}
			return request;
		});
	}

	// provide token to backend
	async RecaptchaP(body: { token: string }): Promise<AxiosResponse> {
		await this.recaptchaT();

		await this.updateFetching('contactFormG', true);

		return axiosInstance.post(this.recaptchaUrl, body, {

			cancelToken: this.cancelToken('recaptchaT'),
			transformResponse: [this.transformResponse('recaptchaP')]
		});
	}

	async ContactP(params: FormDataType): Promise<AxiosResponse> {
		const formData = {
			...params
		};
		await this.contactT();

		await this.updateFetching('contactP', true);

		return axiosInstance.post(this.contactUrl, formData, {
			cancelToken: this.cancelToken('contactT'),
			transformResponse: [this.transformResponse('contactP')]
		});
	}

	async GetAppP(body: {phone:{prefix: string, number: string}, token: string}): Promise<AxiosResponse> {
		await this.getAppT();

		await this.updateFetching('getAppP', true);

		return axiosInstance.post(this.getAppUrl, body, {
			cancelToken: this.cancelToken('getAppT'),
			transformResponse: [this.transformResponse('getAppP')]
		});
	}

	// updates specified key in store with values true / false
	// depending on request either beeing executed or was done
	async updateFetching(key: string, state: boolean): Promise<void> {
		return await this.$store.dispatch('updateFetching', {key, state});
	}

	// cancel token is used to abort previous request with the same request url
	cancelToken(name: string): CancelToken {
		return new axios.CancelToken((cancel: () => void) => {
			// @ts-ignore
			this[name] = cancel;
		});
	}

	// nested then() does not work, using transformResponse
	// method to check whenever is request complete
	transformResponse(name: string): any {
		return (data: string) => {
			this.updateFetching(name, false);

			try {
				return JSON.parse(data);
			} catch (error) {
				return data;
			}
		};
	}

	// input validation
	// field must have ref with `Field` string in its name
	// field names can be modified in `this.defaultFieldSuffix`
	fieldInvalid(fields?: string[]): boolean {
		let validation = [];

		if (fields) {
			validation = fields.map((modelName: string) => `${modelName}${this.defaultFieldSuffix}`);
		} else {
			validation = Object.keys(this.$refs);
		}

		validation = validation.filter((fieldName: string) => {
			return fieldName.indexOf(this.defaultFieldSuffix);
		})
			.map((fieldName: string) => {
				// @ts-ignore
				const validate = this.$refs[fieldName].validate;

				return validate ? validate() : true;
			});

		return !validation.every((state: boolean) => state);
	}

	fieldPrefill(name: string, value: string): void {
		//@ts-ignore
		this[name] = value;
		// @ts-ignore
		this.$refs[`${name}${this.defaultFieldSuffix}`].$refs.inputField.value = value;
		//@ts-ignore
		this.$refs[`${name}${this.defaultFieldSuffix}`].value = value;
	}

	catchException(error: AxiosError, callback: Function = (e: ApiException) => e): void {
		console.warn('ApiException', error);

		const {response} = error;

		if (!response) {
			callback(this.defaultException);

			return;
		}

		const {data} = response;
		const {status, code, message} = data;

		callback({status, code, message});
	}
}
